import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTutorial } from "../context/TutorialContext";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, X } from "lucide-react";

/**
 * Tour script — each step describes one stop on the live tour.
 *
 *   route        — pathname to navigate to before showing the step.
 *                  null = stay on the current page.
 *   target       — CSS selector for the highlighted element.
 *                  null = centred modal (welcome / closing screens).
 *   placement    — "top" / "bottom" / "left" / "right" / "center"
 *                  (auto-flips at viewport edges).
 *   title        — bold one-line headline.
 *   body         — 1-2 warm sentences.
 *
 * All highlighted targets are existing data-testid'd elements — the
 * tour NEVER mutates them, only points at them.
 */
const TOUR_STEPS = [
  {
    key: "welcome",
    route: "/beit-midrash",
    target: null,
    placement: "center",
    titleKey: "tutorial.step1_title_with_name",
    titleKeyNoName: "tutorial.step1_title_default",
    bodyKey: "tutorial.step1_body",
    ctaKey: "tutorial.step1_cta",
  },
  {
    key: "library",
    route: "/beit-midrash",
    target: '[data-testid="bm-continue-card"]',
    placement: "bottom",
    titleKey: "tutorial.step2_title",
    bodyKey: "tutorial.step2_body",
  },
  {
    key: "beit-midrash",
    route: "/beit-midrash",
    target: '[data-testid="bm-card-highlights"]',
    placement: "top",
    titleKey: "tutorial.step3_title",
    bodyKey: "tutorial.step3_body",
  },
  {
    key: "daily",
    route: "/beit-midrash",
    target: '[data-testid="bm-card-daily"]',
    placement: "top",
    titleKey: "tutorial.step4_title",
    bodyKey: "tutorial.step4_body",
  },
  {
    key: "chavruta",
    route: "/beit-midrash",
    target: '[data-testid="chavruta-bubble"]',
    placement: "left",
    titleKey: "tutorial.step5_title",
    bodyKey: "tutorial.step5_body",
  },
  {
    key: "basket",
    route: "/beit-midrash",
    target: '[data-testid="bm-card-build-lesson"]',
    placement: "top",
    titleKey: "tutorial.step6_title",
    bodyKey: "tutorial.step6_body",
  },
  {
    key: "notebook",
    route: "/beit-midrash",
    target: '[data-testid="bm-card-notebook"]',
    placement: "top",
    titleKey: "tutorial.step7_title",
    bodyKey: "tutorial.step7_body",
  },
  {
    key: "share",
    route: "/beit-midrash",
    target: '[data-testid="bm-card-share"]',
    placement: "top",
    titleKey: "tutorial.step8_title",
    bodyKey: "tutorial.step8_body",
  },
  {
    key: "closing",
    route: "/beit-midrash",
    target: null,
    placement: "center",
    titleKey: "tutorial.step9_title",
    bodyKey: "tutorial.step9_body",
    ctaKey: "tutorial.step9_cta",
    isFinal: true,
  },
];

const TOOLTIP_W = 320;          // max width of tooltip card
const TOOLTIP_GAP = 14;         // gap between target and tooltip
const SPOTLIGHT_PADDING = 8;    // halo padding around target

export default function TutorialOverlay() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const { active, step, next, stop } = useTutorial();
  const { completeOnboarding, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [rect, setRect] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const stepRef = useRef(step);
  stepRef.current = step;

  // Pre-compute first name once, since hooks can't be called inside
  // conditional JSX branches (Rules of Hooks).
  const firstName = user?.name ? String(user.name).trim().split(/\s+/)[0] : "";

  const current = active && step < TOUR_STEPS.length ? TOUR_STEPS[step] : null;

  // Navigate to step's route (best-effort) when the step changes.
  // Avoid re-pushing the same path because that would force a remount of
  // the destination page mid-tour.
  useEffect(() => {
    if (!current) return;
    if (current.route && location.pathname !== current.route) {
      navigate(current.route);
    }
  }, [current, navigate, location.pathname]);

  // Resolve the target element + watch for layout changes. We poll for
  // 2.5s because nav state (route change, lazy-loaded image, etc.) may
  // delay the target element from appearing. Once resolved we attach a
  // ResizeObserver so the spotlight stays glued through window resizes
  // and the BeitMidrash card grid reflowing under it.
  useLayoutEffect(() => {
    if (!current) {
      setRect(null);
      return;
    }
    if (current.target == null) {
      setRect("center"); // sentinel: centred modal
      return;
    }
    setRect(null);

    let cancelled = false;
    let ro = null;
    let pollTimer = null;
    let polls = 0;
    const MAX_POLLS = 25; // 25 × 100ms = 2.5s

    const measureAndSet = (el) => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        setRect({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
          right: r.right,
          bottom: r.bottom,
        });
        return true;
      }
      return false;
    };

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(current.target);
      if (el && measureAndSet(el)) {
        // Re-measure on resize / scroll so the spotlight stays glued.
        ro = new ResizeObserver(() => {
          if (!cancelled) measureAndSet(el);
        });
        ro.observe(el);
        // Scroll the target into view if it's off-screen.
        const r = el.getBoundingClientRect();
        if (r.top < 50 || r.bottom > window.innerHeight - 50) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }
      polls += 1;
      if (polls >= MAX_POLLS) {
        // Target never showed up — fall back to centred modal so the
        // tour can still progress without trapping the user.
        setRect("center");
        return;
      }
      pollTimer = setTimeout(tick, 100);
    };
    pollTimer = setTimeout(tick, 100);

    const onScroll = () => {
      const el = document.querySelector(current.target);
      if (el) measureAndSet(el);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (ro) ro.disconnect();
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [current]);

  const finishTour = async () => {
    if (finishing) return;
    setFinishing(true);
    try {
      await completeOnboarding();
    } finally {
      stop();
      setFinishing(false);
      // Land the user on the library (the same destination as the
      // closing screen of the old onboarding) so the tour transitions
      // smoothly into the real app instead of dumping them on the
      // current page mid-action.
      navigate("/library", { replace: true });
    }
  };

  const handleNext = () => {
    if (!current) return;
    if (current.isFinal) {
      finishTour();
      return;
    }
    next();
  };

  if (!active || !current) return null;

  const isCenter = rect === "center" || current.target == null;
  const isLoadingRect = !isCenter && rect == null;

  // Resolve title/body/cta from i18n keys. Special-case the welcome
  // step which interpolates the user's first name.
  const titleText = (() => {
    if (current.key === "welcome") {
      return firstName
        ? t(current.titleKey, { name: firstName })
        : t(current.titleKeyNoName);
    }
    return t(current.titleKey);
  })();
  const bodyText = t(current.bodyKey);
  const ctaText = current.ctaKey ? t(current.ctaKey) : t("tutorial.next");

  // ---------------- centred modal ----------------
  if (isCenter) {
    return (
      <div
        data-testid="tutorial-overlay"
        className="tutorial-overlay fixed inset-0 z-[100] flex items-center justify-center px-4"
        dir={isEn ? "ltr" : "rtl"}
      >
        <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" />
        <div
          data-testid={`tutorial-step-${current.key}`}
          className="relative z-10 tutorial-card max-w-md w-full px-7 py-8 sm:px-9 sm:py-10"
        >
          <button
            type="button"
            onClick={finishTour}
            disabled={finishing}
            data-testid="tutorial-close"
            aria-label={t("tutorial.close_aria")}
            className={`absolute top-3 ${isEn ? "right-3" : "left-3"} text-muted-foreground hover:text-foreground p-1 rounded-sm`}
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <h2 className="font-heading text-2xl sm:text-3xl text-foreground text-center" data-testid="tutorial-title">
            {titleText}
          </h2>
          <p className="text-center text-sm sm:text-base text-muted-foreground mt-4 leading-relaxed" data-testid="tutorial-body">
            {bodyText}
          </p>
          <div className="flex flex-col gap-2 mt-7">
            <button
              type="button"
              onClick={handleNext}
              disabled={finishing}
              data-testid="tutorial-next"
              className="sacred-btn-gold inline-flex items-center justify-center gap-2 py-3 px-6 rounded-sm font-heading"
            >
              {finishing ? t("tutorial.opening") : ctaText}
              <ArrowLeft className={`h-4 w-4 ${isEn ? "rotate-180" : ""}`} strokeWidth={1.75} />
            </button>
            {!current.isFinal && (
              <button
                type="button"
                onClick={finishTour}
                disabled={finishing}
                data-testid="tutorial-skip"
                className="text-xs sm:text-sm text-muted-foreground hover:text-foreground py-2"
              >
                {t("tutorial.skip_long")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------------- spotlight overlay ----------------
  // While we're still resolving the target, show a soft dim background
  // (no spotlight cutout) so the user sees that *something* is loading.
  if (isLoadingRect) {
    return (
      <div data-testid="tutorial-overlay-loading" className="fixed inset-0 z-[100] bg-black/35 backdrop-blur-[1px]" />
    );
  }

  // Pad the spotlight rectangle by SPOTLIGHT_PADDING on each side.
  const spot = {
    top: Math.max(0, rect.top - SPOTLIGHT_PADDING),
    left: Math.max(0, rect.left - SPOTLIGHT_PADDING),
    width: rect.width + SPOTLIGHT_PADDING * 2,
    height: rect.height + SPOTLIGHT_PADDING * 2,
  };
  spot.right = spot.left + spot.width;
  spot.bottom = spot.top + spot.height;

  // Tooltip placement — auto-flip if there isn't room.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let place = current.placement || "bottom";
  // For "bottom" preferred: if target's bottom > vh - 160, flip to "top".
  if (place === "bottom" && spot.bottom > vh - 200) place = "top";
  if (place === "top" && spot.top < 200) place = "bottom";
  if (place === "left" && spot.left < TOOLTIP_W + 40) place = "bottom";
  if (place === "right" && spot.right + TOOLTIP_W + 40 > vw) place = "bottom";

  let tipStyle = {};
  const TOOLTIP_H = 180;  // conservative tooltip height for clamping
  if (place === "bottom") {
    tipStyle.top = Math.min(
      Math.max(12, spot.bottom + TOOLTIP_GAP),
      vh - TOOLTIP_H - 12
    );
    tipStyle.left = Math.min(
      Math.max(12, spot.left + spot.width / 2 - TOOLTIP_W / 2),
      vw - TOOLTIP_W - 12
    );
  } else if (place === "top") {
    // Use top + transform pattern (not bottom) so we can clamp cleanly.
    tipStyle.top = Math.min(
      Math.max(12, spot.top - TOOLTIP_GAP - TOOLTIP_H),
      vh - TOOLTIP_H - 12
    );
    tipStyle.left = Math.min(
      Math.max(12, spot.left + spot.width / 2 - TOOLTIP_W / 2),
      vw - TOOLTIP_W - 12
    );
  } else if (place === "left") {
    tipStyle.right = Math.min(
      Math.max(12, vw - spot.left + TOOLTIP_GAP),
      vw - TOOLTIP_W - 12
    );
    tipStyle.top = Math.min(
      Math.max(12, spot.top + spot.height / 2 - 80),
      vh - TOOLTIP_H - 12
    );
  } else if (place === "right") {
    tipStyle.left = Math.min(
      Math.max(12, spot.right + TOOLTIP_GAP),
      vw - TOOLTIP_W - 12
    );
    tipStyle.top = Math.min(
      Math.max(12, spot.top + spot.height / 2 - 80),
      vh - TOOLTIP_H - 12
    );
  }

  return (
    <div
      data-testid="tutorial-overlay"
      className="fixed inset-0 z-[100]"
      dir={isEn ? "ltr" : "rtl"}
    >
      {/* Four dark strips around the spotlight create the cutout effect.
          Each strip captures clicks so nothing outside is interactive
          during the tour. */}
      <div
        className="tutorial-strip absolute bg-black/55 backdrop-blur-[1px]"
        style={{ top: 0, left: 0, right: 0, height: spot.top }}
      />
      <div
        className="tutorial-strip absolute bg-black/55 backdrop-blur-[1px]"
        style={{ top: spot.top, left: 0, width: spot.left, height: spot.height }}
      />
      <div
        className="tutorial-strip absolute bg-black/55 backdrop-blur-[1px]"
        style={{ top: spot.top, left: spot.right, right: 0, height: spot.height }}
      />
      <div
        className="tutorial-strip absolute bg-black/55 backdrop-blur-[1px]"
        style={{ top: spot.bottom, left: 0, right: 0, bottom: 0 }}
      />
      {/* Gold halo ring around the spotlight — purely decorative,
          non-interactive (pointer-events:none) so the user can still
          click "Next" through it. */}
      <div
        className="tutorial-spotlight-ring"
        style={{
          top: spot.top, left: spot.left,
          width: spot.width, height: spot.height,
          pointerEvents: "none",
        }}
        aria-hidden
      />
      {/* Click-blocker INSIDE the spotlight: prevents the user from
          accidentally activating the highlighted element mid-tour, which
          could navigate them away and break the spotlight tracking. */}
      <div
        className="absolute"
        style={{
          top: spot.top, left: spot.left,
          width: spot.width, height: spot.height,
          background: "transparent",
        }}
        onClick={(e) => e.stopPropagation()}
        aria-hidden
      />
      {/* Tooltip card */}
      <div
        data-testid={`tutorial-step-${current.key}`}
        className="tutorial-card absolute z-[110]"
        dir={isEn ? "ltr" : "rtl"}
        style={{ ...tipStyle, width: TOOLTIP_W, maxWidth: "calc(100vw - 24px)" }}
      >
        <div className="px-5 py-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-heading text-base text-foreground" data-testid="tutorial-title">
              {titleText}
            </h3>
            <button
              type="button"
              onClick={finishTour}
              disabled={finishing}
              data-testid="tutorial-close"
              aria-label={t("tutorial.close_aria")}
              className="text-muted-foreground hover:text-foreground p-1 -m-1 rounded-sm"
            >
              <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="tutorial-body">
            {bodyText}
          </p>
          <div className="flex items-center justify-between mt-4 gap-2">
            <button
              type="button"
              onClick={finishTour}
              disabled={finishing}
              data-testid="tutorial-skip"
              className="text-xs text-muted-foreground hover:text-foreground py-1.5 px-2"
            >
              {t("tutorial.skip_short")}
            </button>
            <div className="flex items-center gap-1.5">
              {TOUR_STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all ${
                    i === step ? "w-4 bg-gold" : "w-1.5 bg-foreground/20"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={handleNext}
              disabled={finishing}
              data-testid="tutorial-next"
              className="inline-flex items-center gap-1 text-xs font-heading bg-gold/15 text-gold border border-gold/40 hover:bg-gold/25 py-1.5 px-3 rounded-sm"
            >
              {t("tutorial.next")}
              <ArrowLeft className={`h-3 w-3 ${isEn ? "rotate-180" : ""}`} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
